// The row of tabs under a section's page title (docs/plans/nav-tabs.md): Sales, Purchase
// orders, Bills and payments and a restaurant's Menu. Which tabs show is lib/sections.ts;
// the counts come from statements the pages run anyway, never a new one in the shell.
import { cx } from '@ims/ui'
import { Link, useLocation } from 'react-router'
import { useBusinessMode, useFeature, useLive } from '../hooks.ts'
import { todayIso } from '../lib/format.ts'
import { moveClick } from '../lib/motion.ts'
import { addDays, QUOTE_SOON_DAYS } from '../lib/sales-orders.ts'
import { TO_ORDER_TABLES } from '../screens/reorder.tsx'
import { type Section, type SectionAccess, sectionTabs, tabActive } from '../lib/sections.ts'
import { roleAtLeast, useRepo, useRole } from '../workspace.tsx'

/** Purchase control, approval steps and their counts: shared by the sidebar and the tabs. */
export function usePurchaseAccess() {
  const role = useRole()
  const purchaseControl = useFeature('purchase_control')
  const summary = useLive(
    (r) =>
      purchaseControl && r.purchases.approvalSummary
        ? r.purchases.approvalSummary()
        : Promise.resolve(null),
    [purchaseControl],
    [
      'purchase_orders',
      'purchase_order_lines',
      'purchase_requests',
      'purchase_request_lines',
      'approval_rules',
    ],
  ).data
  const approvals = (summary?.rules ?? 0) > 0
  const canApprove = roleAtLeast(role, 'manager')
  return {
    purchaseControl,
    approvals,
    /** Orders waiting for this user's approval (managers, where approval is in use). */
    approvalsWaiting: approvals && canApprove ? (summary?.waiting ?? 0) : 0,
    /** Approved requests with something left to order. */
    requestsToOrder: summary?.requestsToOrder ?? 0,
  }
}

type Tone = 'sell' | 'warn' | 'danger' | 'ok' | 'neutral'
const TONE: Record<Tone, string> = {
  sell: 'bg-sell-light text-sell',
  warn: 'bg-warning-light text-warning',
  danger: 'bg-danger-light text-danger',
  ok: 'bg-success-light text-success',
  neutral: 'bg-soft text-muted',
}

const LABEL: Record<Section, string> = {
  sales: 'Invoices, orders and quotations',
  purchase: 'Purchase orders, what to order, requests and approvals',
  bills: 'Received stock and payments made',
  menu: 'Menu and ingredients',
}

/** The tabs of a section, with what each one counts. */
function useCounts(section: Section, local: boolean) {
  const today = todayIso()
  const purchase = usePurchaseAccess()
  const sales = section === 'sales'
  const buying = section === 'purchase'
  const orders = useLive(
    (r) => (sales && r.salesOrders.counts?.('order', today, today)) || Promise.resolve(null),
    [sales, today],
    ['sales_orders', 'sales_order_lines', 'sales_invoices', 'sales_invoice_lines'],
  ).data
  const quotes = useLive(
    (r) =>
      (sales && r.salesOrders.counts?.('quotation', today, addDays(today, QUOTE_SOON_DAYS))) ||
      Promise.resolve(null),
    [sales, today],
    ['sales_orders', 'sales_order_lines', 'sales_invoices', 'sales_invoice_lines'],
  ).data
  const po = useLive(
    (r) => (buying && r.purchases.orderCounts?.(today)) || Promise.resolve(null),
    [buying, today],
    ['purchase_orders', 'purchase_order_lines', 'goods_receipts', 'goods_receipt_lines'],
  ).data
  const toOrder = useLive(
    (r) => (buying && local && r.purchases.toOrder?.()) || Promise.resolve(null),
    [buying, local],
    [...TO_ORDER_TABLES],
  ).data
  const bills = useLive(
    (r) => (section === 'bills' && r.payables ? r.payables.totals(today) : Promise.resolve(null)),
    [section, today],
    ['supplier_bills', 'supplier_payments', 'supplier_credits', 'supplier_allocations'],
  ).data
  const count: Record<string, [number, Tone, string?]> = {
    '/orders': [(orders?.accepted ?? 0) + (orders?.part ?? 0), 'sell', 'to bill'],
    '/quotations': [quotes?.waiting ?? 0, 'neutral', 'open'],
    '/purchase-orders': [po?.ready ?? 0, 'sell', 'ready to send'],
    '/purchase-orders?show=reorder': [toOrder?.length ?? 0, 'warn'],
    '/purchase-requests': [purchase.requestsToOrder, 'ok', 'to order'],
    '/approvals': [purchase.approvalsWaiting, 'warn', 'for you'],
    '/purchases': [bills?.lateBills ?? 0, 'danger', 'late'],
  }
  return { purchase, count }
}

/**
 * The section's tabs under its page title. Each is a link to its own address, so Back,
 * reload and a shared link keep the tab. Nothing shows when the user has one tab or none.
 */
export function SectionTabs({ section }: { section: Section }) {
  const role = useRole()
  const { restaurant, menu } = useBusinessMode()
  const { pathname, search } = useLocation()
  // What to order reads the local database; the online-only repository has no toOrder.
  const local = !!useRepo().purchases.toOrder
  const { purchase, count } = useCounts(section, local)
  const access: SectionAccess = {
    role,
    restaurant,
    menu,
    purchaseControl: purchase.purchaseControl,
    approvals: purchase.approvals,
    local,
  }
  const tabs = sectionTabs(section, access)
  if (tabs.length < 2) return null
  return (
    <nav
      aria-label={LABEL[section]}
      className="-mt-2 mb-4 flex gap-6 overflow-x-auto border-b border-border [scrollbar-width:none] max-sm:-mx-4 max-sm:px-4"
    >
      {tabs.map((tab) => {
        const on = tabActive(tab, pathname, search)
        const [n, tone, word] = count[tab.to] ?? [0, 'neutral']
        return (
          <Link
            key={tab.to}
            to={tab.to}
            aria-current={on ? 'page' : undefined}
            onClick={moveClick(tab.to, 'section')}
            className={cx(
              '-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-0.5 pt-1 pb-2.5 text-sm transition-colors',
              on
                ? 'border-primary font-bold text-primary'
                : 'border-transparent font-semibold text-muted hover:text-ink',
            )}
          >
            {tab.label}
            {/* The space keeps the count apart in the link's name: "What to order 2". */}
            {n > 0 && ' '}
            {n > 0 && (
              <span
                className={cx('tabular rounded-full px-1.5 text-xs font-bold', TONE[tone])}
                title={word ? `${n} ${word}` : undefined}
              >
                {n}
                {word && <span className="max-sm:hidden"> {word}</span>}
              </span>
            )}
          </Link>
        )
      })}
    </nav>
  )
}
